/* eslint-disable max-len */

import { AugmentedUser, ActiveLog } from 'types/avatarSheet';

/**
 * Maps a V2 open-timer (flat shape from GET /timers) into the nested ActiveLog
 * shape consumed by the team-activity page and the avatar sheet. Live timers are
 * always task-sourced; `customer_*` / `request_*` are enrichment fields the BE
 * derives from the task's project/linked request.
 */
export const mapTimerToActiveLog = (raw: any): ActiveLog => ({
  uuid: raw.uuid,
  status: raw.status,
  started_at: raw.started_at ?? null,
  minutes: raw.minutes ?? 0,
  user: raw.user_uuid ? { uuid: raw.user_uuid } : undefined,
  task: raw.source === 'task'
    ? {
        uuid: raw.source_uuid,
        title: raw.source_title ?? '',
        project: raw.customer_uuid || raw.customer_name
          ? { customer: { uuid: raw.customer_uuid, business_name: raw.customer_name } }
          : undefined,
        requests: raw.request_uuid
          ? [{ uuid: raw.request_uuid, subject: raw.request_subject ?? '' }]
          : undefined,
      }
    : undefined,
});

export const buildAugmentedUsers = (
  rawUsers: any[],
  timeLogs: any[],
  pausedLogs?: any[]
): AugmentedUser[] => {
  const augmentedUsers: AugmentedUser[] = rawUsers
    .filter((user: any) => user && user.uuid)
    .map((user: any) => {
    const activeLog = timeLogs.find((log: any) => log.user?.uuid === user.uuid) || null;
    const userPausedLogs = pausedLogs ? pausedLogs.filter((log: any) => log.user?.uuid === user.uuid) : [];

    let resolvedStatus: 'online' | 'away' | 'offline' = 'offline';
    if (user.activity_status) {
      resolvedStatus = user.activity_status;
    }

    return {
      uuid: user.uuid,
      name: user.name,
      email: user.email,
      avatar: user.avatar || null,
      last_activity: user.last_activity,
      activity_status: user.activity_status,
      resolvedStatus,
      wonTask: null,
      activeLog,
      pausedLogs: userPausedLogs.length > 0 ? userPausedLogs : undefined,
    };
  });

  // Sort: users with active time logs first, then alphabetically
  augmentedUsers.sort((a, b) => {
    // Priority: users with active logs first
    const aHasLog = a.activeLog ? 0 : 1;
    const bHasLog = b.activeLog ? 0 : 1;

    if (aHasLog !== bHasLog) {
      return aHasLog - bHasLog;
    }

    // Then sort alphabetically by name
    return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
  });

  return augmentedUsers;
};

export const sortAugmentedUsers = (users: AugmentedUser[]): AugmentedUser[] => {
  const sorted = [...users];
  sorted.sort((a, b) => {
    // Priority: users with active logs first
    const aHasLog = a.activeLog ? 0 : 1;
    const bHasLog = b.activeLog ? 0 : 1;

    if (aHasLog !== bHasLog) {
      return aHasLog - bHasLog;
    }

    // Then sort alphabetically by name
    return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
  });

  return sorted;
};

export const groupUsersByStatus = (
  users: AugmentedUser[]
): Record<'online' | 'away' | 'offline', AugmentedUser[]> => {
  const grouped: Record<'online' | 'away' | 'offline', AugmentedUser[]> = {
    online: [],
    away: [],
    offline: [],
  };

  users.forEach((user) => {
    grouped[user.resolvedStatus].push(user);
  });

  // Sort each group alphabetically (users with active logs have priority within their status group)
  Object.keys(grouped).forEach((status) => {
    grouped[status as 'online' | 'away' | 'offline'] = sortAugmentedUsers(grouped[status as 'online' | 'away' | 'offline']);
  });

  return grouped;
};
